import React from "react";
import "./App.css";
import { BrowserRouter, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useOnboardingArtifacts } from "./hooks/useOnboardingArtifacts";
import { SecurityProvider } from "./context/SecurityContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import SessionLockModal from "./components/SessionLockModal";
import SessionExpiredBanner from "./components/SessionExpiredBanner";
import SudoModeModal from "./components/SudoModeModal";
import SessionTimeoutHandler from "./components/SessionTimeoutHandler";
import OrderNotificationProvider from "./components/OrderNotification";
import ProtectedRouteComponent from "./components/ProtectedRoute";
import BuildVersionWatcher from "./components/BuildVersionWatcher";
import TrialGate from "./components/TrialGate";
import InstallAppBanner from "./components/InstallAppBanner";
import EnablePushAlertsBanner from "./components/EnablePushAlertsBanner";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { Navigate, Route } from "react-router-dom";
import ActiveSubscriptions from "./pages/customer/ActiveSubscriptions";

// Route groups
import { AuthRoutes } from "./routes/authRoutes";
import { ListingRoutes } from "./routes/listingRoutes";
import { OrderRoutes } from "./routes/orderRoutes";
import { MarketingRoutes } from "./routes/marketingRoutes";
import { VendorRoutes } from "./routes/vendorRoutes";
import { AdminRoutes } from "./routes/adminRoutes";

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  // iter-230j — Artifact-driven onboarding verdict. Hook is safe to call
  // unconditionally; it no-ops for non-vendors and returns instantly from
  // the sessionStorage cache after the first fetch.
  const { artifacts, loading: artifactsLoading } = useOnboardingArtifacts();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    const currentPath = location.pathname + location.search;
    if (currentPath !== '/login' && currentPath !== '/' && currentPath.includes('/vendor/')) {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
    }
    return <Navigate to="/login" />;
  }

  // iter-230p — HOLD RENDER while a vendor's artifact verdict is
  // genuinely in-flight (no cache hit AND API still resolving).
  //
  // Why: vendors with inconsistent legacy flags (`status='pending'` AND
  // `onboarding_completed=false` AND `registration_step >= 6` — i.e.
  // tax registration is actually complete but the flag was never
  // flipped) used to enter a tug-of-war during the ~1-3s artifact
  // fetch window:
  //   - PrivateRoute fallback (legacy `status`-based) said "incomplete"
  //     → redirect to /vendor/tax-registration.
  //   - VendorTaxRegistration mount-effect saw `registration_step >= 6`
  //     → redirect to /dashboard.
  //   - Dashboard's PrivateRoute → still no artifacts → fallback fires
  //     again → back to /vendor/tax-registration.
  // That ping-pong at browser-redirect speed was the violent 0.5s
  // flicker captured on video for Niddhi Jobanputra.
  //
  // Returning `null` (not a spinner element) here is deliberate:
  //   * No alternate JSX tree → no parent element-type swap → React
  //     does NOT unmount/remount TrialGate/children when the verdict
  //     arrives (avoids the iter-230j-v2 remount cascade).
  //   * The route's children simply mount once when artifacts resolve.
  //   * Sub-second invisible hold under normal network conditions.
  // iter-230ab — Strict artifact-loading hold. Previously `!artifacts`
  // gated the hold so the FIRST render after login waited, but every
  // SUBSEQUENT refetch (e.g. after `updateUser` runs in the tax-reg
  // completion handler) reused the stale verdict and let the legacy
  // fallback fire briefly with last user's data. Holding on the
  // loading flag alone is safe: artifactsLoading goes false only when
  // the hook has a fresh verdict for THIS user.
  if (user.role === 'vendor' && artifactsLoading) {
    return null;
  }

  // iter-230j (v2) — ARTIFACT-DRIVEN onboarding lock with the polarity
  // fixed.
  //
  // The previous lock keyed only on `user.status in {pending, …}` AND
  // `!user.onboarding_completed`. That's flag-driven — if either flag
  // drifted (status reassigned by an admin, onboarding_completed flipped
  // by a script), a vendor with NO actual tax_registration / pickup
  // address / bank account slipped onto the dashboard silently.
  //
  // The first iter-230j cut over-corrected: it treated the artifact
  // verdict as STRICTER than the flag, so legitimately-completed
  // vendors whose legacy artifacts didn't match the new strict shape
  // (e.g. `tax_registration.pan_number` missing because they used the
  // old PAN-only tier, or `vendor_pickup_addresses` row missing because
  // they're a service-only seller) were re-locked onto onboarding.
  //
  // It ALSO gated the entire render on `artifactsLoading`, which added
  // an extra spinner→content tree-transition. Combined with React 18
  // StrictMode's double-effect-mount in dev, the child page
  // (VendorTaxRegistration) mounted twice and fired its hydrate
  // useEffect 2-4× per page load — the user-visible flickering.
  //
  // Correct polarity (v2):
  //   1. `user.onboarding_completed === true` → TRUST the flag. Pass
  //      through. Never second-guess a green flag with a stricter
  //      artifact check.
  //   2. `user.onboarding_completed === false` AND artifacts say
  //      complete → AUTO-HEAL: pass through.
  //   3. `user.onboarding_completed === false` AND artifacts say
  //      incomplete → lock onto onboarding routes.
  //   4. No verdict yet (still loading or API errored) → fall back to
  //      legacy status-based lock. This avoids holding the render on a
  //      spinner, which is what caused the page-remount flicker.
  const isVendor = user.role === 'vendor';
  const onboardingAllowedPaths = [
    '/vendor/tax-registration', '/verify-identity', '/set-password',
    '/onboarding-questions', '/vendor/subscription-hub', '/profile',
    '/change-password', '/setup-account', '/setup-password',
    // iter-230au — Defense-in-depth: `compute_onboarding_artifacts` can
    // return any of these as `next_step_route` for incomplete vendors.
    // If one of them is NOT in this allowlist, Navigate->re-evaluate
    // becomes an infinite loop (Chrome eventually throttles, vendor
    // sees a flicker). Add them all proactively.
    '/vendor/payouts', '/vendor/shipping-settings', '/vendor/subscription',
  ];
  const isAllowed = onboardingAllowedPaths.some(p => location.pathname.startsWith(p));

  if (isVendor) {
    let isIncomplete;
    if (user.onboarding_completed === true) {
      // Authoritative: trust the flag. Backwards-compatible with every
      // existing completed vendor in the DB.
      //
      // iter-230am — EXCEPT when backend artifacts have loaded and
      // EXPLICITLY say `completed === false`. That contradiction means
      // the cached `onboarding_completed=true` flag is stale (most
      // commonly: vendor signed up under the old /register-vendor that
      // wrongly auto-marked them complete when they filled the intent
      // wizard). Backend's real-time artifact check is authoritative
      // over a sessionStorage-cached flag. Without this guard,
      // affected vendors had to log out + back in to recover.
      //
      // iter-230au — BUT not for `status === 'active'` vendors. An
      // admin-approved vendor is DEFINITIVELY done with onboarding —
      // an artifact-collection drift (bank row migrated to a legacy
      // table, pickup `is_primary` flag accidentally unset, subscription
      // row deleted by a Phase-D/E cleanup, etc.) must NOT bounce
      // them back to onboarding. Doing so produced the "dashboard
      // flickers post-login" bug (Navigate → /vendor/payouts → still
      // incomplete → Navigate again → Chrome navigation throttling).
      // Admin approval is the trust anchor; the flag for active
      // vendors is treated as authoritative.
      if (user.status === 'active') {
        isIncomplete = false;
      } else if (artifacts && artifacts.completed === false) {
        isIncomplete = true;
      } else {
        isIncomplete = false;
      }
    } else if (artifacts && artifacts.completed === true) {
      // Flag stale but real artifacts say done → auto-heal, pass through.
      isIncomplete = false;
    } else if (artifacts && artifacts.completed === false) {
      // Authoritative: backend verdict from real artifacts.
      isIncomplete = true;
    } else {
      // No verdict yet (artifactsLoading) OR API errored — fall back
      // to legacy status check. This evaluates IMMEDIATELY without
      // gating the render on a spinner, eliminating the extra
      // mount cycle that previously produced the flicker. When the
      // verdict eventually arrives, this component re-renders and the
      // authoritative branches above take over.
      isIncomplete =
        ['pending', 'PENDING_ONBOARDING', 'pending_onboarding'].includes(user.status) &&
        !user.onboarding_completed;
    }

    if (isIncomplete && !isAllowed) {
      const redirectPath =
        artifacts?.next_step_route ||
        localStorage.getItem('onboarding_redirect') ||
        '/vendor/tax-registration';
      return <Navigate to={redirectPath} replace />;
    }
  }

  // Reference `artifactsLoading` so React still subscribes to the hook's
  // state updates (verdict arrival re-renders this component). Without
  // a read the linter could later warn it's unused and we'd lose the
  // subscription dependency.
  void artifactsLoading;

  return <TrialGate>{children}</TrialGate>;
};

function App() {
  return (
    <AuthProvider>
    <ThemeProvider>
    <CurrencyProvider>
    <SecurityProvider>
      <BrowserRouter>
        <Routes>
          {/* Public customer self-service portal (magic-link or App Proxy) */}
          <Route path="/portal/subscriptions" element={<ActiveSubscriptions />} />
          {AuthRoutes()}
          {ListingRoutes({ PrivateRoute })}
          {OrderRoutes({ PrivateRoute })}
          {MarketingRoutes({ PrivateRoute, ProtectedRoute: ProtectedRouteComponent })}
          {VendorRoutes({ PrivateRoute })}
          {AdminRoutes({ PrivateRoute, ProtectedRoute: ProtectedRouteComponent })}
          {/* iter-230g — Catch-all so an unknown path NEVER renders blank.
              New vendors on mobile previously hit `/verify-identity` (no
              route) → React Router rendered nothing → blank screen. The
              named alias above handles that specific case, this catch-all
              ensures any future redirect_path renames also fail safely. */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster />
        {/* iter-296 — sonner Toaster was never mounted although 61 components
            fire `toast.*` from 'sonner' (admin Sync-from-Razorpay, cancel,
            change-plan, refund, etc.) — all their feedback was silent. */}
        <SonnerToaster richColors position="top-right" />
        <SessionTimeoutHandler />
        <SessionLockModal />
        {/* iter-232.J — Global impersonation banner. Mounted at the
            App root so it shows above every route during an admin's
            vendor-impersonation session and persists across
            navigations. Also surfaces the "session expired" state
            when AuthContext refuses to refresh an impersonation
            token (the fix that prevents silent swap back to admin). */}
        <ImpersonationBanner />
        {/* iter-230ab — Inline session-expired banner for vendors. Sits
            above all routes; shows ONLY when AuthContext flips
            `sessionExpired=true` after a 401-cascade. Replaces the
            hard-navigate-to-/login that caused the dashboard ↔ login
            flicker in the production video. */}
        <SessionExpiredBanner />
        <SudoModeModal />
        <OrderNotificationProvider />
        <BuildVersionWatcher />
        <InstallAppBanner />
        <EnablePushAlertsBanner />
      </BrowserRouter>
    </SecurityProvider>
    </CurrencyProvider>
    </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
